import { create } from "zustand";

export type CameraOverride = {
  position?: [number, number, number];
  target?: [number, number, number];
  transitionDuration?: number;
  rotationEuler?: [number, number, number];
};

export type InteractionState = {
  activePromptId: string | null;
  promptText: string;
  promptVisible: boolean;
  hudMessage: string | null;
  hudTimeoutId: number | null;
  hudPersistent: boolean;
  playerHidden: boolean;
  playerOpacityTarget: number;
  movementLocked: boolean;
  playerFrozen: boolean;
  cameraLocked: boolean;
  cameraOverride: CameraOverride | null;
  shopVisible: boolean;
  shopLockSnapshot: { movementLocked: boolean; playerFrozen: boolean } | null;
  activatePrompt: (id: string, text: string) => void;
  deactivatePrompt: (id: string) => void;
  showHudMessage: (text: string, duration?: number | null) => void;
  clearHudMessage: () => void;
  setPlayerHidden: (hidden: boolean) => void;
  togglePlayerHidden: () => boolean;
  setCameraOverride: (locked: boolean, override?: CameraOverride | null) => void;
  showShop: () => void;
  hideShop: () => void;
  setMovementLocked: (locked: boolean) => void;
  setPlayerFrozenState: (frozen: boolean) => void;
};

const PROMPT_DEFAULT_TEXT = "Interactuar [E]";
const HUD_DEFAULT_DURATION = 3000;

export const useInteractionStore = create<InteractionState>((set, get) => ({
  activePromptId: null,
  promptText: PROMPT_DEFAULT_TEXT,
  promptVisible: false,
  hudMessage: null,
  hudTimeoutId: null,
  hudPersistent: false,
  playerHidden: false,
  playerOpacityTarget: 1,
  movementLocked: false,
  playerFrozen: false,
  cameraLocked: false,
  cameraOverride: null,
  shopVisible: false,
  shopLockSnapshot: null,
  activatePrompt: (id: string, text: string) => {
    const { activePromptId, promptText } = get();
    if (activePromptId === id && promptText === text) return;
    set({ activePromptId: id, promptText: text, promptVisible: true });
  },
  deactivatePrompt: (id: string) => {
    if (get().activePromptId !== id) return;
    set({ activePromptId: null, promptText: PROMPT_DEFAULT_TEXT, promptVisible: false });
  },
  showHudMessage: (text: string, duration: number | null = HUD_DEFAULT_DURATION) => {
    if (!text) {
      get().clearHudMessage();
      return;
    }

    const { hudTimeoutId } = get();
    if (hudTimeoutId !== null) {
      clearTimeout(hudTimeoutId);
    }

    const isPersistent =
      duration === null || duration === undefined || !Number.isFinite(duration) || duration <= 0;

    if (isPersistent) {
      set({ hudMessage: text, hudTimeoutId: null, hudPersistent: true });
      return;
    }

    const timeoutId = window.setTimeout(() => {
      set({ hudMessage: null, hudTimeoutId: null, hudPersistent: false });
    }, duration);

    set({ hudMessage: text, hudTimeoutId: timeoutId, hudPersistent: false });
  },
  clearHudMessage: () => {
    const { hudTimeoutId } = get();
    if (hudTimeoutId !== null) {
      clearTimeout(hudTimeoutId);
    }
    set({ hudMessage: null, hudTimeoutId: null, hudPersistent: false });
  },
  setPlayerHidden: (hidden: boolean) => {
    set({
      playerHidden: hidden,
      playerOpacityTarget: hidden ? 0 : 1,
      movementLocked: hidden,
      playerFrozen: hidden,
    });
  },
  togglePlayerHidden: () => {
    const { playerHidden, setPlayerHidden } = get();
    const nextValue = !playerHidden;
    setPlayerHidden(nextValue);
    return nextValue;
  },
  setCameraOverride: (locked: boolean, override: CameraOverride | null = null) => {
    if (!locked) {
      set({ cameraLocked: false, cameraOverride: null });
      return;
    }

    const transitionDuration = override?.transitionDuration ?? 0.75;

    set({
      cameraLocked: true,
      cameraOverride: {
        position: override?.position,
        target: override?.target,
        transitionDuration,
        rotationEuler: override?.rotationEuler,
      },
    });
  },
  showShop: () => {
    const exitPointerLock = () => {
      if (typeof document === "undefined") return;
      if (document.pointerLockElement && typeof document.exitPointerLock === "function") {
        try {
          document.exitPointerLock();
        } catch {
          /* noop */
        }
      }
    };

    exitPointerLock();

    set((state) => ({
      shopVisible: true,
      shopLockSnapshot:
        state.shopLockSnapshot ??
        (state.playerHidden
          ? { movementLocked: false, playerFrozen: false }
          : { movementLocked: state.movementLocked, playerFrozen: state.playerFrozen }),
      movementLocked: true,
      playerFrozen: true,
    }));
  },
  hideShop: () => {
    const { setCameraOverride, clearHudMessage } = get();
    setCameraOverride(false);
    clearHudMessage();

    set((state) => {
      const snapshot =
        state.shopLockSnapshot ?? { movementLocked: false, playerFrozen: false };
      return {
        shopVisible: false,
        shopLockSnapshot: null,
        playerHidden: false,
        playerOpacityTarget: 1,
        movementLocked: snapshot.movementLocked,
        playerFrozen: snapshot.playerFrozen,
      };
    });
  },
  setMovementLocked: (locked: boolean) => {
    set({ movementLocked: locked });
  },
  setPlayerFrozenState: (frozen: boolean) => {
    set({ playerFrozen: frozen });
  },
}));
